import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { HTTPError } from "ky";
import ConnectionError from "../../components/ConnectionError/ConnectionError";
import EmptyState from "../../components/EmptyState/EmptyState";
import { Modal } from "../../components/common/Modal/Modal";
import QuotationForm from "../../components/QuotationForm/QuotationForm";
import { productService } from "../../services/productService";
import { publicContentService } from "../../services/publicContentService";
import { resolveApiUrl } from "../../services/api";
import useReveal from "../../hooks/useReveal";
import type { ProductItem, ServiceItem } from "../../types";
import "./ProductDetail.css";

function ProductDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const isArabic = i18n.resolvedLanguage?.startsWith("ar") ?? false;

  const [product, setProduct] = useState<ProductItem | null>(null);
  const [service, setService] = useState<ServiceItem | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const loadProduct = useCallback(async () => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    setNotFound(false);
    try {
      const data = await productService.getProduct(id);
      setProduct(data);
      setActiveImage(0);
    } catch (err) {
      if (err instanceof HTTPError && err.response.status === 404) {
        setNotFound(true);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  // Resolve the parent service title for the breadcrumb (services list is small)
  useEffect(() => {
    if (!product?.serviceId) return;
    let cancelled = false;
    publicContentService
      .getServices()
      .then((services) => {
        if (!cancelled) {
          setService(services.find((s) => s.id === product.serviceId) || null);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [product?.serviceId]);

  useReveal([loading]);

  if (loading) {
    return (
      <main className="page-main">
        <section className="section">
          <div className="container">
            <div className="pdp-layout">
              <div className="pdp-skeleton pdp-skeleton--gallery" />
              <div className="pdp-skeleton-col">
                <div className="pdp-skeleton pdp-skeleton--title" />
                <div className="pdp-skeleton pdp-skeleton--line" />
                <div className="pdp-skeleton pdp-skeleton--line" />
                <div className="pdp-skeleton pdp-skeleton--line short" />
                <div className="pdp-skeleton pdp-skeleton--btn" />
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (notFound || (!error && !product)) {
    return (
      <main className="page-main">
        <section className="section">
          <div className="container">
            <EmptyState
              title={t("products.productNotFound")}
              description={t("products.productNotFoundDesc")}
            />
            <div className="catalog-notfound-action">
              <Link to="/services" className="btn btn-outline">
                {t("products.backToServices")}
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="page-main">
        <section className="section">
          <div className="container">
            <ConnectionError onRetry={loadProduct} />
          </div>
        </section>
      </main>
    );
  }

  const name = isArabic ? product.nameAr : product.nameEn;
  const desc = isArabic ? product.descAr : product.descEn;
  const size = isArabic ? product.sizeAr : product.sizeEn;
  const serviceTitle = service ? (isArabic ? service.titleAr : service.titleEn) : "";
  const images = product.imageUrls || [];
  const mainImage = images[Math.min(activeImage, images.length - 1)];

  return (
    <main className="page-main">
      <section className="section pdp-section">
        <div className="container">
          {/* Breadcrumb */}
          <nav className="catalog-breadcrumb reveal" aria-label="Breadcrumb">
            <Link to="/services">{t("products.backToServices")}</Link>
            {service && (
              <>
                <span className="catalog-breadcrumb-sep">/</span>
                <Link to={`/services/${product.serviceId}`}>{serviceTitle}</Link>
              </>
            )}
            <span className="catalog-breadcrumb-sep">/</span>
            <span className="catalog-breadcrumb-current">{name}</span>
          </nav>

          <div className="pdp-layout">
            {/* Gallery */}
            <div className="pdp-gallery reveal">
              <div className="pdp-main-image">
                {mainImage ? (
                  <img src={resolveApiUrl(mainImage)} alt={name} />
                ) : (
                  <div className="pdp-main-image-placeholder">
                    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="pdp-thumbs">
                  {images.map((url, index) => (
                    <button
                      key={url + index}
                      type="button"
                      className={`pdp-thumb ${index === activeImage ? "pdp-thumb--active" : ""}`}
                      onClick={() => setActiveImage(index)}
                      aria-label={`${name} ${index + 1}`}
                    >
                      <img src={resolveApiUrl(url)} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="pdp-details reveal reveal-delay-2">
              <h1 className="pdp-name">{name}</h1>

              {size && (
                <div className="pdp-size-block">
                  <span className="pdp-size-label">{t("products.size")}</span>
                  <span className="pdp-size-value">{size}</span>
                </div>
              )}

              {desc && (
                <div className="pdp-desc-block">
                  <h2 className="pdp-block-title">{t("products.description")}</h2>
                  <p className="pdp-desc">{desc}</p>
                </div>
              )}

              <button
                type="button"
                className="btn btn-filled pdp-cta"
                onClick={() => setQuoteModalOpen(true)}
              >
                {t("products.requestQuotation")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Modal
        isOpen={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
        title={t("quotation.title")}
        className="quotation-modal"
      >
        <QuotationForm
          productId={product.id}
          productName={name}
          onSuccess={() => setQuoteModalOpen(false)}
          onCancel={() => setQuoteModalOpen(false)}
        />
      </Modal>
    </main>
  );
}

export default ProductDetail;
